import ActiveToggleButton from "@/components/ActiveToggleButton";
import { BaseTable } from "@/components/BaseTable";
import CustomButton from "@/components/common/Button";
import TenantPermissionService from "@/services/APIs/TenantPermissionService";
import { gridName } from "@/utils/constant/gridName";
import { tableName } from "@/utils/constant/tableName";
import { Checkbox, Form, Popconfirm } from "antd";
import { Edit2, Trash } from "lucide-react";
import { useState } from "react";
import PermissionModal from "./PermissionModal";

interface PermissionsTableProps {
  permissions: any;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  permissionsLoading: boolean;
  roleOptions: any[];
  schemaOptions: any[];
  toolBarRender: () => React.ReactNode[];
  refetchPermissions: () => void;
}

const PermissionsTable = ({
  permissions,
  page,
  pageSize,
  onPageChange,
  permissionsLoading,
  roleOptions,
  toolBarRender,
  refetchPermissions,
}: PermissionsTableProps) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<any>(null);
  const getRoleMasterById = (id: string) => {
    return roleOptions?.find(
      (role: { value: string; label: string }) => role.value === id
    );
  };

  const handleEdit = (record: any) => {
    setEditingPermission(record.props.text);
    setEditModalOpen(true);
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
    setEditingPermission(null);
  };

  const handleEditSuccess = () => {
    setEditModalOpen(false);
    setEditingPermission(null);
  };

  const handleDelete = async (record: any) => {
    const response = await TenantPermissionService.deletePermission(
      record.props.text.id
    );
    if (response) {
      refetchPermissions();
    }
  };

  const permissionColumns = [
    {
      title: "Schema",
      dataIndex: "schema_name",
      sorter: (a: any, b: any) => a.schema_name!.localeCompare(b.schema_name!),
    },
    {
      title: "Role",
      render: (record: any) => {
        return (
          <>
            <div>{getRoleMasterById(record.role)?.label}</div>
            <Form.Item name="only_if_created" label="Only If Created">
              <Checkbox checked={record.only_if_created} />
            </Form.Item>
            <Form.Item label="Is Active">
              <ActiveToggleButton
                table={tableName.permission}
                pk={record.id}
                is_active={record.is_active}
              />
            </Form.Item>
          </>
        );
      },
    },
    {
      title: "Level",
      dataIndex: "level",
    },
    {
      title: "Permissions",
      render: (record: any) => {
        return (
          <div className="permission-checkboxes">
            <p>Permissions</p>
            <div>
              <Form.Item name="can_read" label="Read">
                <Checkbox checked={record.can_read} />
              </Form.Item>

              <Form.Item name="can_write" label="Write">
                <Checkbox checked={record.can_write} />
              </Form.Item>

              <Form.Item name="can_create" label="Create">
                <Checkbox checked={record.can_create} />
              </Form.Item>

              <Form.Item name="can_delete" label="Delete">
                <Checkbox checked={record.can_delete} />
              </Form.Item>
            </div>

            <p>Additional Permissions</p>
            <div>
              <Form.Item name="can_select" label="Select">
                <Checkbox checked={record.can_select} />
              </Form.Item>
              <Form.Item name="can_print" label="Print">
                <Checkbox checked={record.can_print} />
              </Form.Item>

              <Form.Item name="can_export" label="Export">
                <Checkbox checked={record.can_export} />
              </Form.Item>

              <Form.Item name="can_import" label="Import">
                <Checkbox checked={record.can_import} />
              </Form.Item>

              <Form.Item name="can_email" label="Email">
                <Checkbox checked={record.can_email} />
              </Form.Item>

              <Form.Item name="can_report" label="Report">
                <Checkbox checked={record.can_report} />
              </Form.Item>

              <Form.Item name="can_share" label="Share">
                <Checkbox checked={record.can_share} />
              </Form.Item>
            </div>
          </div>
        );
      },
    },
    {
      title: "Actions",
      width: 120,
      valueType: "option",
      render: (record: any) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={14} />}
          type="text"
          size="small"
          onClick={() => handleEdit(record)}
          title="Edit Permission"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this permission?"
          onConfirm={() => handleDelete(record)}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            icon={<Trash size={14} />}
            type="text"
            size="small"
            danger
            title="Delete Permission"
          />
        </Popconfirm>,
      ],
    },
  ];

  return (
    <>
      <BaseTable
        loading={permissionsLoading}
        columns={permissionColumns as any}
        dataSource={permissions?.results ?? []}
        headerTitle="Permissions Table"
        page={page}
        pageSize={pageSize}
        total={permissions?.count ?? 0}
        onPageChange={(page, size) => {
          onPageChange(page, size ?? 10);
        }}
        rowKey="id"
        tableName={gridName.permissionTable}
        request={refetchPermissions as any}
        toolBarRender={toolBarRender}
      />

      <PermissionModal
        open={editModalOpen}
        onCancel={handleEditCancel}
        onSuccess={handleEditSuccess}
        mode="edit"
        permissionId={editingPermission?.id ?? ""}
        initialData={editingPermission}
        refetchPermissions={refetchPermissions}
        selectedSchema={editingPermission?.schema_name ?? ""}
        selectedRole={editingPermission?.role ?? ""}
      />
    </>
  );
};

export default PermissionsTable;
